import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  Bell,
  Pencil,
  Plus,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import wingitDesign from '../designSystem';

const priorityOptions = [
  {
    value: 'information',
    label: 'Information',
  },
  {
    value: 'reminder',
    label: 'Reminder',
  },
  {
    value: 'important',
    label: 'Important',
  },
];

const priorityTone = {
  information: {
    label: 'Information',
    color: '#57d5ff',
    background: 'rgba(87,213,255,0.10)',
    border: 'rgba(87,213,255,0.30)',
  },
  reminder: {
    label: 'Reminder',
    color: '#f2b84b',
    background: 'rgba(242,184,75,0.10)',
    border: 'rgba(242,184,75,0.30)',
  },
  important: {
    label: 'Important',
    color: '#f26d6d',
    background: 'rgba(242,109,109,0.10)',
    border: 'rgba(242,109,109,0.30)',
  },
};

export default function AnnouncementsPanel({
  organisationId,
  user,
}) {
  const styles = getStyles();

  const resolvedOrganisationId = String(
    organisationId || ''
  ).trim();

  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [statusIsError, setStatusIsError] =
    useState(false);

  const [draft, setDraft] = useState('');
  const [priority, setPriority] =
    useState('information');
  const [expiresAt, setExpiresAt] = useState('');

  const [editingId, setEditingId] = useState('');
  const [editingMessage, setEditingMessage] =
    useState('');
  const [editingPriority, setEditingPriority] =
    useState('information');
  const [editingExpiresAt, setEditingExpiresAt] =
    useState('');

  const loadNotices = useCallback(async () => {
    setLoading(true);
    setStatus('');
    setStatusIsError(false);

    if (!resolvedOrganisationId) {
      setNotices([]);
      setStatus(
        'Could not identify the current organisation.'
      );
      setStatusIsError(true);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('club_notices')
      .select(
        'id, message, priority, expires_at, created_at, organisation_id, created_by'
      )
      .eq(
        'organisation_id',
        resolvedOrganisationId
      )
      .order('created_at', {
        ascending: false,
      });

    if (error) {
      setNotices([]);
      setStatus('Could not load announcements.');
      setStatusIsError(true);
      setLoading(false);
      return;
    }

    setNotices(data || []);
    setLoading(false);
  }, [resolvedOrganisationId]);

  useEffect(() => {
    loadNotices();
  }, [loadNotices]);

  async function addNotice() {
    const message = draft.trim();

    if (!message) {
      setStatus('Please enter an announcement.');
      setStatusIsError(true);
      return;
    }

    if (!resolvedOrganisationId) {
      setStatus(
        'Could not identify the current organisation.'
      );
      setStatusIsError(true);
      return;
    }

    setSaving(true);
    setStatus('');
    setStatusIsError(false);

    const { error } = await supabase
      .from('club_notices')
      .insert([
        {
          message,
          priority,
          expires_at: expiresAt || null,
          organisation_id:
            resolvedOrganisationId,
          created_by: user?.id || null,
        },
      ]);

    if (error) {
      setStatus('Could not create announcement.');
      setStatusIsError(true);
      setSaving(false);
      return;
    }

    setDraft('');
    setPriority('information');
    setExpiresAt('');
    setStatus('Announcement created.');
    setSaving(false);

    await loadNotices();
    setStatus('Announcement created.');
  }

  function startEditing(notice) {
    setEditingId(String(notice.id));
    setEditingMessage(notice.message || '');
    setEditingPriority(
      notice.priority || 'information'
    );
    setEditingExpiresAt(notice.expires_at || '');
    setStatus('');
    setStatusIsError(false);
  }

  function cancelEditing() {
    setEditingId('');
    setEditingMessage('');
    setEditingPriority('information');
    setEditingExpiresAt('');
  }

  async function saveNotice(id) {
    const message = editingMessage.trim();

    if (!message) {
      setStatus('Please enter an announcement.');
      setStatusIsError(true);
      return;
    }

    if (!resolvedOrganisationId) {
      setStatus(
        'Could not identify the current organisation.'
      );
      setStatusIsError(true);
      return;
    }

    setSaving(true);
    setStatus('');
    setStatusIsError(false);

    const { error } = await supabase
      .from('club_notices')
      .update({
        message,
        priority: editingPriority,
        expires_at: editingExpiresAt || null,
      })
      .eq('id', id)
      .eq(
        'organisation_id',
        resolvedOrganisationId
      );

    if (error) {
      setStatus('Could not update announcement.');
      setStatusIsError(true);
      setSaving(false);
      return;
    }

    cancelEditing();
    setSaving(false);

    await loadNotices();
    setStatus('Announcement updated.');
  }

  async function deleteNotice(id) {
    if (!resolvedOrganisationId) {
      setStatus(
        'Could not identify the current organisation.'
      );
      setStatusIsError(true);
      return;
    }

    const confirmed = window.confirm(
      'Delete this announcement?'
    );

    if (!confirmed) return;

    setSaving(true);
    setStatus('');
    setStatusIsError(false);

    const { error } = await supabase
      .from('club_notices')
      .delete()
      .eq('id', id)
      .eq(
        'organisation_id',
        resolvedOrganisationId
      );

    if (error) {
      setStatus('Could not delete announcement.');
      setStatusIsError(true);
      setSaving(false);
      return;
    }

    if (String(editingId) === String(id)) {
      cancelEditing();
    }

    setSaving(false);

    await loadNotices();
    setStatus('Announcement deleted.');
  }

  function isExpired(expiresDate) {
    if (!expiresDate) return false;

    const today = new Date()
      .toISOString()
      .split('T')[0];

    return expiresDate < today;
  }

  return (
    <section style={styles.panel}>
      <div style={styles.header}>
        <div style={styles.iconWrap}>
          <Bell size={20} strokeWidth={2.2} />
        </div>

        <div>
          <h3 style={styles.title}>
            Announcements
          </h3>

          <p style={styles.subtitle}>
            Create and manage messages shown on the
            member dashboard.
          </p>
        </div>
      </div>

      <div style={styles.createArea}>
        <label style={styles.field}>
          <span style={styles.label}>
            Announcement
          </span>

          <textarea
            value={draft}
            onChange={(event) =>
              setDraft(event.target.value)
            }
            placeholder="Enter an announcement for members..."
            rows={3}
            style={styles.textarea}
          />
        </label>

        <div style={styles.formRow}>
          <label style={styles.field}>
            <span style={styles.label}>
              Priority
            </span>

            <select
              value={priority}
              onChange={(event) =>
                setPriority(event.target.value)
              }
              style={styles.input}
            >
              {priorityOptions.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label style={styles.field}>
            <span style={styles.label}>
              Expiry date
            </span>

            <input
              type="date"
              value={expiresAt}
              onChange={(event) =>
                setExpiresAt(event.target.value)
              }
              style={styles.input}
            />
          </label>

          <button
            type="button"
            onClick={addNotice}
            disabled={saving}
            style={{
              ...styles.primaryButton,
              ...(saving
                ? styles.disabledButton
                : {}),
            }}
          >
            <Plus size={16} />
            Add announcement
          </button>
        </div>
      </div>

      {status && (
        <div
          style={{
            ...styles.status,
            color: statusIsError
              ? '#ffb0b0'
              : '#9ff2cf',
          }}
        >
          {status}
        </div>
      )}

      <div style={styles.listHeading}>
        Existing announcements
      </div>

      {loading ? (
        <div style={styles.empty}>
          Loading announcements...
        </div>
      ) : notices.length === 0 ? (
        <div style={styles.empty}>
          No announcements have been created.
        </div>
      ) : (
        <div style={styles.list}>
          {notices.map((notice) => {
            const tone =
              priorityTone[notice.priority] ||
              priorityTone.information;

            const expired = isExpired(
              notice.expires_at
            );

            const isEditing =
              String(editingId) ===
              String(notice.id);

            return (
              <div
                key={notice.id}
                style={{
                  ...styles.notice,
                  borderColor: tone.border,
                  background: tone.background,
                  opacity: expired ? 0.72 : 1,
                }}
              >
                {isEditing ? (
                  <div style={styles.editArea}>
                    <textarea
                      value={editingMessage}
                      onChange={(event) =>
                        setEditingMessage(
                          event.target.value
                        )
                      }
                      rows={3}
                      style={styles.textarea}
                    />

                    <div style={styles.formRow}>
                      <label style={styles.field}>
                        <span style={styles.label}>
                          Priority
                        </span>

                        <select
                          value={editingPriority}
                          onChange={(event) =>
                            setEditingPriority(
                              event.target.value
                            )
                          }
                          style={styles.input}
                        >
                          {priorityOptions.map(
                            (option) => (
                              <option
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </option>
                            )
                          )}
                        </select>
                      </label>

                      <label style={styles.field}>
                        <span style={styles.label}>
                          Expiry date
                        </span>

                        <input
                          type="date"
                          value={editingExpiresAt}
                          onChange={(event) =>
                            setEditingExpiresAt(
                              event.target.value
                            )
                          }
                          style={styles.input}
                        />
                      </label>

                      <button
                        type="button"
                        onClick={() =>
                          saveNotice(notice.id)
                        }
                        disabled={saving}
                        style={{
                          ...styles.primaryButton,
                          ...(saving
                            ? styles.disabledButton
                            : {}),
                        }}
                      >
                        <Save size={16} />
                        Save
                      </button>

                      <button
                        type="button"
                        onClick={cancelEditing}
                        disabled={saving}
                        style={styles.secondaryButton}
                      >
                        <X size={16} />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={styles.noticeLayout}>
                    <div style={styles.noticeContent}>
                      <div
                        style={{
                          ...styles.priority,
                          color: tone.color,
                        }}
                      >
                        {tone.label}
                      </div>

                      <div style={styles.noticeText}>
                        {notice.message}
                      </div>

                      <div style={styles.noticeMeta}>
                        {notice.expires_at
                          ? expired
                            ? `Expired ${notice.expires_at}`
                            : `Expires ${notice.expires_at}`
                          : 'No expiry date'}
                      </div>
                    </div>

                    <div style={styles.actions}>
                      <button
                        type="button"
                        onClick={() =>
                          startEditing(notice)
                        }
                        disabled={saving}
                        style={styles.editButton}
                        aria-label="Edit announcement"
                      >
                        <Pencil size={15} />
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          deleteNotice(notice.id)
                        }
                        disabled={saving}
                        style={styles.deleteButton}
                        aria-label="Delete announcement"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function getStyles() {
  const ds = wingitDesign;

  return {
    panel: {
      ...ds.cards.secondary,
      borderRadius: ds.radius.large,
      padding: '1.1rem',
      boxShadow: ds.shadow.card,
      marginBottom: '1rem',
    },

    header: {
      display: 'flex',
      gap: '0.8rem',
      alignItems: 'center',
      marginBottom: '1rem',
    },

    iconWrap: {
      width: 42,
      height: 42,
      borderRadius: 14,
      border: `1px solid ${ds.colors.border}`,
      background: ds.colors.primarySoft,
      color: ds.colors.primary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },

    title: {
      margin: 0,
      color: ds.colors.text,
      fontSize: '1.1rem',
      fontWeight: 850,
    },

    subtitle: {
      margin: '0.25rem 0 0',
      color: ds.colors.textMuted,
      fontSize: '0.86rem',
      lineHeight: 1.4,
    },

    createArea: {
      borderTop: `1px solid ${ds.colors.border}`,
      paddingTop: '1rem',
      display: 'grid',
      gap: '0.7rem',
    },

    field: {
      display: 'grid',
      gap: '0.3rem',
      minWidth: 150,
      flex: '1 1 170px',
    },

    label: {
      color: ds.colors.textMuted,
      fontSize: '0.76rem',
      fontWeight: 750,
    },

    textarea: {
      width: '100%',
      boxSizing: 'border-box',
      resize: 'vertical',
      borderRadius: ds.radius.medium,
      border: `1px solid ${ds.colors.border}`,
      background: '#101827',
      color: ds.colors.text,
      padding: '0.72rem',
      fontSize: '0.9rem',
      fontFamily: 'inherit',
      outline: 'none',
    },

    input: {
      width: '100%',
      boxSizing: 'border-box',
      background: '#101827',
      color: ds.colors.textSoft,
      border: `1px solid ${ds.colors.border}`,
      borderRadius: ds.radius.medium,
      padding: '0.58rem 0.7rem',
      fontFamily: 'inherit',
    },

    formRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.6rem',
      alignItems: 'flex-end',
    },

    primaryButton: {
      ...ds.buttons.primary,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.4rem',
      padding: '0.62rem 0.85rem',
      fontSize: '0.82rem',
      minHeight: 38,
    },

    secondaryButton: {
      ...ds.buttons.secondary,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.4rem',
      padding: '0.62rem 0.85rem',
      fontSize: '0.82rem',
      minHeight: 38,
    },

    disabledButton: {
      opacity: 0.55,
      cursor: 'not-allowed',
    },

    status: {
      marginTop: '0.8rem',
      fontSize: '0.86rem',
    },

    listHeading: {
      color: ds.colors.textSoft,
      fontWeight: 800,
      fontSize: '0.86rem',
      margin: '1rem 0 0.6rem',
    },

    list: {
      display: 'grid',
      gap: '0.6rem',
    },

    empty: {
      color: ds.colors.textMuted,
      fontSize: '0.88rem',
      background: 'rgba(150,176,207,0.06)',
      border: `1px solid ${ds.colors.border}`,
      borderRadius: ds.radius.medium,
      padding: '0.9rem',
    },

    notice: {
      border: '1px solid',
      borderRadius: ds.radius.medium,
      padding: '0.8rem',
      boxShadow:
        'inset 0 1px 0 rgba(255,255,255,0.035)',
    },

    noticeLayout: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '0.8rem',
    },

    noticeContent: {
      minWidth: 0,
    },

    priority: {
      fontSize: '0.68rem',
      fontWeight: 900,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      marginBottom: 5,
    },

    noticeText: {
      color: ds.colors.text,
      fontSize: '0.92rem',
      lineHeight: 1.42,
      whiteSpace: 'pre-wrap',
    },

    noticeMeta: {
      color: ds.colors.textMuted,
      fontSize: '0.74rem',
      marginTop: 6,
    },

    actions: {
      display: 'flex',
      gap: '0.4rem',
      flexShrink: 0,
    },

    editButton: {
      width: 34,
      height: 34,
      background: 'rgba(87,213,255,0.10)',
      color: '#9ae8ff',
      border: '1px solid rgba(87,213,255,0.30)',
      borderRadius: 999,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
    },

    deleteButton: {
      width: 34,
      height: 34,
      background: 'rgba(242,109,109,0.12)',
      color: '#ffb0b0',
      border: '1px solid rgba(242,109,109,0.35)',
      borderRadius: 999,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
    },

    editArea: {
      display: 'grid',
      gap: '0.65rem',
    },
  };
}
